import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import AdminChatBot from '../components/AdminChatBot/AdminChatBot';
import {
  Briefcase,
  FileText,
  Users,
  Bell,
  BarChart2,
  Building,
  Settings,
  List,
  MessageSquare,
  Edit,
  Upload,
  Image,
  UserPlus,
  X,
  ChevronDown,
  GraduationCap,
  User,
} from "lucide-react";

const SidebarLink = ({ title, icon, link, active }) => (
  <Link
    to={link}
    className={`flex items-center px-6 py-3 text-sm ${
      active
        ? "bg-primary text-white"
        : "text-gray-700 hover:bg-primary/10 hover:text-primary"
    } transition-colors`}
  >
    <span className="mr-3">{icon}</span>
    {title}
  </Link>
);

const AdminLayout = ({ children }) => {
  const location = useLocation();
  const currentPath = location.pathname;
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { currentUser } = useAuth();

  return (
    <div className="min-h-screen bg-gray-50 relative">

      
      {/* Admin Mobile Toggle */}
      {!isMenuOpen && (
        <div className="sm:hidden sticky top-0 left-0 right-0 bg-white border-b border-gray-200 shadow-sm z-40">
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="w-full p-3 flex items-center justify-between text-primary hover:bg-gray-50"
            aria-label="Toggle admin menu"
          >
            <span className="flex items-center">
              <Settings size={20} className="mr-2" />
              <span className="font-medium">Admin Panel</span>
            </span>
            {isMenuOpen ? <X size={20} /> : <ChevronDown size={20} />}
          </button>
        </div>
      )}

      <div className="flex min-h-screen">
        {/* Sidebar */}
        <aside className={`${
          isMenuOpen ? 'translate-x-0' : '-translate-x-full'
        } sm:translate-x-0 transform transition-transform duration-200 ease-in-out fixed sm:sticky top-0 left-0 w-64 bg-white shadow-md min-h-screen z-30 overflow-y-auto lg:block`}>
          <div className="px-6 py-4 border-b border-gray-200">
            <h1 className="text-xl font-bold text-primary">Admin Dashboard</h1>
            <p className="text-sm text-gray-500">Admin Panel</p>
          </div>

          {/* Admin User Info */}
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center space-x-3">
              <div className="flex-shrink-0">
                <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center">
                  <User className="w-6 h-6 text-orange-500" />
                </div>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">
                  {currentUser?.displayName || 'Admin User'}
                </p>
                <p className="text-sm text-gray-500 truncate">
                  {currentUser?.email || 'admin@example.com'}
                </p>
              </div>
            </div>
          </div>

          <nav className="mt-6">
            <SidebarLink
              title="Dashboard Overview"
              icon={<BarChart2 size={20} />}
              link="/admin-dashboard"
              active={currentPath === '/admin-dashboard'}
            />
            <SidebarLink
              title="Job Listings"
              icon={<Briefcase size={20} />}
              link="/admin/job-listings"
              active={currentPath === '/admin/job-listings'}
            />
            <SidebarLink
              title="Applications"
              icon={<FileText size={20} />}
              link="/admin/applications"
              active={currentPath === '/admin/applications'}
            />
            <SidebarLink
              title="Employer Contacts"
              icon={<Building size={20} />}
              link="/admin/employer-contacts"
              active={currentPath === '/admin/employer-contacts'}
            />
            <SidebarLink
              title="General Inquiries"
              icon={<MessageSquare size={20} />}
              link="/admin/general-inquiries"
              active={currentPath === '/admin/general-inquiries'}
            />
            <SidebarLink
              title="Banner Management"
              icon={<Image size={20} />}
              link="/admin/banners"
              active={currentPath === '/admin/banners'}
            />
            <SidebarLink
              title="Bulletin Management"
              icon={<FileText size={20} />}
              link="/admin/bulletins"
              active={currentPath === '/admin/bulletins'}
            />
            <SidebarLink
              title="Notifications"
              icon={<Bell size={20} />}
              link="/admin/notifications"
              active={currentPath === '/admin/notifications'}
            />
            <SidebarLink
              title="Analytics"
              icon={<BarChart2 size={20} />}
              link="/admin/analytics"
              active={currentPath === '/admin/analytics'}
            />
            
            {/* Employee Management Links */}
            <div className="mt-4 px-6 py-2 text-xs text-gray-500 uppercase font-semibold">
              Employee Management
            </div>
            <SidebarLink
              title="Onboarding Documents"
              icon={<FileText size={20} />}
              link="/admin/onboarding"
              active={currentPath === '/admin/onboarding'}
            />
            <SidebarLink
              title="Employee Referrals"
              icon={<UserPlus size={20} />}
              link="/admin/referrals"
              active={currentPath === '/admin/referrals'}
            />
            <SidebarLink
              title="Resignations"
              icon={<Users size={20} />}
              link="/admin/resignations"
              active={currentPath === '/admin/resignations'}
            />
            <SidebarLink
              title="W2 Upload"
              icon={<Upload size={20} />}
              link="/admin/w2-upload"
              active={currentPath === '/admin/w2-upload'}
            />
            <SidebarLink
              title="W2 Documents"
              icon={<FileText size={20} />}
              link="/admin/w2-documents"
              active={currentPath === '/admin/w2-documents'}
            />
            <SidebarLink
              title="Training Management"
              icon={<GraduationCap size={20} />}
              link="/admin/training"
              active={currentPath === '/admin/training'}
            />
            
            {/* System Management Links */}
            <div className="mt-4 px-6 py-2 text-xs text-gray-500 uppercase font-semibold">
              System Management
            </div>
            <SidebarLink
              title="System Logs"
              icon={<List size={20} />}
              link="/admin/logs"
              active={currentPath === '/admin/logs'}
            />
            <SidebarLink
              title="Blog Management"
              icon={<Edit size={20} />}
              link="/admin/blog"
              active={currentPath === '/admin/blog'}
            />
            <SidebarLink
              title="Settings"
              icon={<Settings size={20} />}
              link="/admin/settings"
              active={currentPath === '/admin/settings'}
            />
          </nav>
        </aside>

        {/* Main Content */}
        {/* Overlay */}
        {isMenuOpen && (
          <div
            className="fixed inset-0 bg-black bg-opacity-50 z-20 sm:hidden"
            onClick={() => setIsMenuOpen(false)}
          ></div>
        )}

        <main className="flex-1 w-full sm:ml-0 min-h-screen bg-gray-50 pt-[48px] sm:pt-0">
          {children}
        </main>
      </div>
      
      <AdminChatBot />
    </div>
  );
};

export default AdminLayout; 